import { createRoot } from "react-dom/client";
import "./styles/legacy-app.less";
import { lazy, Suspense } from "react";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { initAPI } from "./api/_api";
import { Store } from "./store";
import { queryClient } from "./api/query-client";
import App from "./app";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import { FullPageLoading } from "@/components/ui/full-page-loading";
import { reloadPage } from "@/utils/reload-page";
import { initializeBugsnag } from "./services/bugsnag";
import { patchDomForGoogleTranslate } from "./utils/google-translate-patch";

const Observability = lazy(() => import("./utils/observability"));

// Patch DOM for Google Translate compatibility (React 18 workaround)
patchDomForGoogleTranslate();

// Initialize Bugsnag before API and app bootstrap so all runtime reports use config.
initializeBugsnag();

function BootstrapFailureScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-6">
      <div className="max-w-md space-y-4 text-center">
        <i className="fa-regular fa-arrows-rotate text-5xl text-blue-500" />
        <h1 className="font-gotham text-2xl font-semibold text-neutral-900">
          Refresh Required
        </h1>
        <p className="text-sm text-neutral-700">
          Sked could not finish loading safely. Please refresh to re-establish
          your session and try again.
        </p>
        <button
          type="button"
          onClick={reloadPage}
          className="rounded-lg bg-blue-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-600 active:bg-blue-700"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

function renderApp(root: ReturnType<typeof createRoot>) {
  root.render(
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Provider store={Store}>
          <App />
          {/* This is part of a hack to fix bootstrap 3 tooltips not working in react 18
              and relates to the "CustomOverlay" component */}
          <div id="tooltip-root"></div>
          <Suspense fallback={null}>
            <Observability />
          </Suspense>
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

async function bootstrap(root: ReturnType<typeof createRoot>) {
  root.render(<FullPageLoading message="Loading Sked..." />);

  try {
    // Wait for the first CSRF token fetch so startup POSTs cannot race the
    // initial app bootstrap on a cold load.
    await initAPI();
  } catch (error) {
    try {
      const { initializeObservability, reportBootstrapFailure } =
        await import("./utils/observability");
      initializeObservability();
      reportBootstrapFailure(error);
    } catch (observabilityError) {
      console.error(
        "[bootstrap] failed to report bootstrap failure to observability",
        observabilityError
      );
    }

    console.error("[bootstrap] initial API setup failed", error);
    root.render(<BootstrapFailureScreen />);
    return;
  }

  renderApp(root);
}

// Wait for DOM to be ready
document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("react-root");
  if (container) {
    const root = createRoot(container);
    void bootstrap(root);
  }
});
